import { useMemo, useState } from "react";
import { Reveal } from "./Reveal";

const euro = (n) => `€ ${n.toLocaleString("nl-NL")},-`;

const priceOptions = [
  { label: "Elke prijs", value: 0 },
  { label: "Tot € 900", value: 900 },
  { label: "Tot € 1.000", value: 1000 },
  { label: "Tot € 1.100", value: 1100 },
  { label: "Tot € 1.300", value: 1300 },
];

const roomOptions = [
  { label: "Alle kamers", value: 0 },
  { label: "1 kamer", value: 1 },
  { label: "2 kamers", value: 2 },
  { label: "3 kamers", value: 3 },
  { label: "4+ kamers", value: 4 },
];

const selectCls =
  "appearance-none bg-background border border-border px-4 py-3 pr-10 text-sm font-medium outline-none focus:border-foreground transition-colors duration-200 rounded-none cursor-pointer";

const FilterBar = ({ filters, setFilters, cities, count }) => (
  <Reveal delay={0.15}>
    <div
      data-testid="filter-bar"
      className="flex flex-wrap items-end gap-4 mb-14 md:mb-20 border border-border bg-background p-6"
    >
      <div className="flex flex-col gap-2">
        <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">
          Stad
        </label>
        <div className="relative">
          <select
            data-testid="filter-city-select"
            value={filters.city}
            onChange={(e) =>
              setFilters((f) => ({ ...f, city: e.target.value }))
            }
            className={selectCls}
          >
            <option value="">Alle steden</option>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            ↓
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">
          Max. huurprijs
        </label>
        <div className="relative">
          <select
            data-testid="filter-price-select"
            value={filters.maxPrice}
            onChange={(e) =>
              setFilters((f) => ({ ...f, maxPrice: Number(e.target.value) }))
            }
            className={selectCls}
          >
            {priceOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            ↓
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">
          Kamers
        </label>
        <div className="flex border border-border">
          {roomOptions.map((o) => (
            <button
              key={o.value}
              data-testid={`filter-rooms-${o.value}`}
              onClick={() => setFilters((f) => ({ ...f, rooms: o.value }))}
              className={`px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                filters.rooms === o.value
                  ? "bg-foreground text-background"
                  : "bg-background hover:bg-secondary"
              }`}
            >
              {o.value === 0 ? "Alle" : o.value === 4 ? "4+" : o.value}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4 ml-auto">
        <p
          data-testid="filter-result-count"
          className="text-sm text-muted-foreground whitespace-nowrap"
        >
          {count} {count === 1 ? "woning" : "woningen"}
        </p>
        <button
          data-testid="filter-reset-button"
          onClick={() => setFilters({ city: "", maxPrice: 0, rooms: 0 })}
          className="text-sm font-medium underline underline-offset-4 hover:text-muted-foreground transition-colors duration-200"
        >
          Wis filters
        </button>
      </div>
    </div>
  </Reveal>
);

export const Properties = ({ properties, onReact }) => {
  const [filters, setFilters] = useState({ city: "", maxPrice: 0, rooms: 0 });

  const cities = useMemo(
    () => [...new Set(properties.map((p) => p.city))].sort(),
    [properties]
  );

  const filtered = useMemo(
    () =>
      properties.filter(
        (p) =>
          (!filters.city || p.city === filters.city) &&
          (!filters.maxPrice || p.price <= filters.maxPrice) &&
          (!filters.rooms ||
            (filters.rooms === 4 ? p.rooms >= 4 : p.rooms === filters.rooms))
      ),
    [properties, filters]
  );

  return (
    <section
      id="woningen"
      data-testid="properties-section"
      className="py-24 md:py-40 bg-card border-y border-border"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-16">
          <div>
            <Reveal>
              <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-muted-foreground mb-6">
                Actueel aanbod
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-black text-3xl md:text-5xl lg:text-6xl tracking-tight leading-none">
                Betaalbare huurhuizen
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="text-sm text-muted-foreground max-w-xs">
              Voorbeeldaanbod — nieuwe woningen worden wekelijks toegevoegd.
              Reageer direct op een woning die bij je past.
            </p>
          </Reveal>
        </div>

        <FilterBar
          filters={filters}
          setFilters={setFilters}
          cities={cities}
          count={filtered.length}
        />

        {filtered.length === 0 ? (
          <div
            data-testid="filter-empty-state"
            className="border border-border py-24 text-center"
          >
            <p className="font-display font-bold text-2xl tracking-tight">
              Geen woningen gevonden
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              Pas je filters aan of{" "}
              <a
                href="mailto:arthur@rentguru.nl"
                className="underline underline-offset-2"
              >
                mail ons
              </a>{" "}
              met je zoekopdracht.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={0.08 * (i % 3)}>
                <article
                  data-testid={`property-card-${p.id}`}
                  className="group"
                >
                  <div
                    className={`relative overflow-hidden border border-border ${p.aspect}`}
                  >
                    <img
                      src={p.image}
                      alt={`${p.title} — ${p.city}`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <span className="absolute top-4 left-4 bg-foreground text-background text-sm font-medium px-3 py-1.5">
                      {euro(p.price)} <span className="opacity-60">/mnd</span>
                    </span>
                  </div>

                  <div className="pt-5 flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">
                        {p.city}
                      </p>
                      <h3 className="font-display font-bold text-lg md:text-xl tracking-tight mt-1">
                        {p.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-2">
                        {p.rooms} {p.rooms === 1 ? "kamer" : "kamers"} ·{" "}
                        {p.area} m² · {p.available}
                      </p>
                    </div>
                    <button
                      data-testid={`reageer-button-${p.id}`}
                      onClick={() => onReact(p)}
                      className="shrink-0 border border-foreground px-5 py-2.5 text-sm font-medium hover:bg-foreground hover:text-background transition-colors duration-200"
                    >
                      Reageer
                    </button>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
